import React from 'react';
import { History, Users, TrendingUp, Heart } from 'lucide-react';

const OurStory = () => {
  return (
    <section id="about" className="py-20 bg-gradient-to-br from-white via-blue-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="mb-6">
            <p className="text-2xl font-bold text-blue-600 mb-2">Jai Bhim!</p>
            <p className="text-2xl font-bold text-orange-600">Namo Buddhaya!</p>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Story
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            A journey of transformation, dedication, and Buddhist revival
          </p>
        </div>

        {/* Timeline Card */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-t-4 border-blue-600">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <History className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900">Introduction</h3>
                <p className="text-lg text-blue-600 font-semibold">Mass Movement for Transformation</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center text-white">
                    <div className="text-center">
                      <p className="text-3xl font-bold">2015</p>
                      <p className="text-xs">Founded</p>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      <strong>Mass Movement for Transformation (MMT)</strong> was started in 2015 by a group of doctors from <strong>Thanjavur Government Medical College (1988 Batch)</strong>, all from the oppressed community. Our primary objective is creating awareness of the life and mission of <strong>Babasaheb Dr. B.R. Ambedkar</strong> and propagating his ideals and philosophy of establishing an egalitarian society based on <strong>Social Justice, Liberty, and Fraternity</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {/* First Meeting */}
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-6 mb-8 border-l-4 border-orange-600">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-orange-600">⚡</span> The Spark That Ignited the Movement
                </h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The first meeting of like-minded doctors took place in <strong>Trichy</strong>, organized by MMT with <strong>Professor Dr. Satyapal</strong> as the chief guest. Prof. Dr. Satyapal, an Anthropologist and Research Scientist from Andhra University, delivered a transformative lecture on <strong>"BABASAHEB AMBEDKAR WRITINGS AND SPEECHES: Connecting the Importance of Buddhism in the Revival of Salvation Movement to Oppressed Communities of India."</strong>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  He emphasized the need for revolutionary activities for the revival of Buddhism as envisioned by Babasaheb Dr. B.R. Ambedkar. <strong className="text-orange-600">This ignited a light and wisdom in the doctor's community</strong>, setting the foundation for our transformative journey.
                </p>
              </div>

              {/* Expansion */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <TrendingUp className="w-7 h-7 text-green-600" />
                  Expansion & Growth
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600">
                    <h5 className="font-bold text-lg text-gray-900 mb-3">Professional Groups</h5>
                    <p className="text-gray-700 mb-3">In furtherance of our objectives, MMT has formed various professional groups including:</p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        <span className="text-gray-700">Engineers</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        <span className="text-gray-700">Professors</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        <span className="text-gray-700">Teachers</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        <span className="text-gray-700">Government Employees</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        <span className="text-gray-700">Students</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-600">
                    <h5 className="font-bold text-lg text-gray-900 mb-3">Geographic Reach</h5>
                    <p className="text-gray-700 mb-4">MMT expanded its wings by spreading all over <strong>Tamil Nadu</strong> by forming district groups.</p>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="w-6 h-6 text-purple-600" />
                        <h6 className="font-bold text-purple-900">Women's Wing</h6>
                      </div>
                      <p className="text-gray-700 text-sm">MMT has a dedicated Women's Wing with complete organizational structure, empowering women in the Buddhist revival movement.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Principles */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
          <div className="text-center mb-8">
            <Heart className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-3xl font-bold mb-4">Our Core Principles</h3>
            <p className="text-xl text-blue-100">Following Babasaheb Dr. B.R. Ambedkar's Vision</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
              <div className="text-4xl font-bold mb-2">⚖️</div>
              <h4 className="text-xl font-bold mb-2">Social Justice</h4>
              <p className="text-blue-100">Equality and fairness for all members of society</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
              <div className="text-4xl font-bold mb-2">🕊️</div>
              <h4 className="text-xl font-bold mb-2">Liberty</h4>
              <p className="text-blue-100">Freedom from oppression and discrimination</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center">
              <div className="text-4xl font-bold mb-2">🤝</div>
              <h4 className="text-xl font-bold mb-2">Fraternity</h4>
              <p className="text-blue-100">Brotherhood and unity among all people</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStory;